import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

// Local types for the Supabase-based implementation
export interface Plan {
  id: string
  name: string
  slug: string
  description: string | null
  price_monthly_cents: number
  price_yearly_cents: number | null
  currency: string
  billing_model: 'fixed' | 'custom'
  account_scope: string
  max_units: number | null
  max_professionals: number
  max_patients: number
  max_reports_per_month: number | null
  max_storage_mb: number | null
  features: string[]
  trial_days: number
  is_active: boolean
  is_public: boolean
  is_highlighted: boolean
  badge_text: string | null
  display_order: number
  subscriber_count: number
  stripe_product_id: string | null
  stripe_price_monthly_id: string | null
  stripe_price_yearly_id: string | null
  created_at: string
  updated_at: string
}

interface PlansResponse {
  data: Plan[]
}

interface PlanResponse {
  data: Plan
}

interface CreatePlanData {
  name: string
  slug?: string
  description?: string
  price_monthly_cents: number
  price_yearly_cents?: number
  currency?: string
  billing_model?: 'fixed' | 'custom'
  account_scope?: string
  max_units?: number | null
  max_professionals: number
  max_patients: number
  max_reports_per_month?: number | null
  max_storage_mb?: number | null
  features?: string[]
  trial_days?: number
  is_public?: boolean
  is_highlighted?: boolean
  badge_text?: string | null
  display_order?: number
}

interface UpdatePlanData {
  name?: string
  description?: string
  price_monthly_cents?: number
  price_yearly_cents?: number
  billing_model?: 'fixed' | 'custom'
  account_scope?: string
  max_units?: number | null
  max_professionals?: number
  max_patients?: number
  max_reports_per_month?: number | null
  max_storage_mb?: number | null
  features?: string[]
  trial_days?: number
  is_public?: boolean
  is_highlighted?: boolean
  badge_text?: string | null
  display_order?: number
}

export function usePlans(params: { includeInactive?: boolean } = {}) {
  return useQuery<PlansResponse>({
    queryKey: ['super-admin', 'plans', params],
    queryFn: async () => {
      let query = supabase
        .from('subscription_plans')
        .select('*')
        .order('display_order', { ascending: true })

      // Only filter by active if not including inactive
      if (!params.includeInactive) {
        query = query.eq('is_active', true)
      }

      const { data, error } = await query

      if (error) throw error

      const planIds = (data || []).map((plan: any) => plan.id).filter(Boolean)
      let subscriberCounts: Record<string, number> = {}

      if (planIds.length > 0) {
        const { data: subscriptions, error: subscriptionsError } = await supabase
          .from('subscriptions')
          .select('plan_id')
          .in('plan_id', planIds)
          .eq('status', 'active')

        if (subscriptionsError) throw subscriptionsError

        subscriberCounts = (subscriptions || []).reduce<Record<string, number>>((acc, subscription: any) => {
          if (!subscription.plan_id) return acc
          acc[subscription.plan_id] = (acc[subscription.plan_id] || 0) + 1
          return acc
        }, {})
      }

      // Transform data to match expected format
      const plans: Plan[] = (data || []).map((plan: any) => ({
        id: plan.id,
        name: plan.name,
        slug: plan.slug,
        description: plan.description,
        price_monthly_cents: plan.price_monthly_cents || plan.price_cents,
        price_yearly_cents: plan.price_yearly_cents || (plan.price_cents ? plan.price_cents * 10 : null),
        currency: plan.currency || 'BRL',
        billing_model: plan.billing_model || 'fixed',
        account_scope: plan.account_scope || 'Conta clínica',
        max_units: plan.max_units ?? null,
        max_professionals: plan.max_professionals,
        max_patients: plan.max_patients,
        max_reports_per_month: plan.max_reports_per_month,
        max_storage_mb: plan.max_storage_mb,
        features: plan.features || [],
        trial_days: plan.trial_days || 14,
        is_active: plan.is_active,
        is_public: plan.is_public,
        is_highlighted: plan.is_highlighted,
        badge_text: plan.badge_text,
        display_order: plan.display_order,
        subscriber_count: subscriberCounts[plan.id] || 0,
        stripe_product_id: plan.stripe_product_id,
        stripe_price_monthly_id: plan.stripe_price_monthly_id,
        stripe_price_yearly_id: plan.stripe_price_yearly_id,
        created_at: plan.created_at,
        updated_at: plan.updated_at,
      }))

      return { data: plans }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

export function usePlan(id: string) {
  return useQuery<PlanResponse>({
    queryKey: ['super-admin', 'plan', id],
    queryFn: async () => {
      const { data: plan, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error

      return {
        data: {
          id: plan.id,
          name: plan.name,
          slug: plan.slug,
          description: plan.description,
          price_monthly_cents: plan.price_monthly_cents || plan.price_cents,
          price_yearly_cents: plan.price_yearly_cents,
          currency: plan.currency || 'BRL',
          billing_model: plan.billing_model || 'fixed',
          account_scope: plan.account_scope || 'Conta clínica',
          max_units: plan.max_units ?? null,
          max_professionals: plan.max_professionals,
          max_patients: plan.max_patients,
          max_reports_per_month: plan.max_reports_per_month,
          max_storage_mb: plan.max_storage_mb,
          features: plan.features || [],
          trial_days: plan.trial_days || 14,
          is_active: plan.is_active,
          is_public: plan.is_public,
          is_highlighted: plan.is_highlighted,
          badge_text: plan.badge_text,
          display_order: plan.display_order,
          subscriber_count: 0,
          stripe_product_id: plan.stripe_product_id,
          stripe_price_monthly_id: plan.stripe_price_monthly_id,
          stripe_price_yearly_id: plan.stripe_price_yearly_id,
          created_at: plan.created_at,
          updated_at: plan.updated_at,
        },
      }
    },
    enabled: !!id,
  })
}

export function useCreatePlan() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreatePlanData) => {
      const slug = data.slug || data.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')

      const { data: plan, error } = await supabase
        .from('subscription_plans')
        .insert({
          name: data.name,
          slug,
          description: data.description,
          price_monthly_cents: data.price_monthly_cents,
          price_yearly_cents: data.price_yearly_cents,
          price_cents: data.price_monthly_cents, // backward compat
          currency: data.currency || 'BRL',
          billing_model: data.billing_model || 'fixed',
          account_scope: data.account_scope || 'Conta clínica',
          max_units: data.max_units ?? null,
          max_professionals: data.max_professionals,
          max_patients: data.max_patients,
          max_reports_per_month: data.max_reports_per_month,
          max_storage_mb: data.max_storage_mb,
          features: data.features || [],
          trial_days: data.trial_days || 14,
          is_active: true,
          is_public: data.is_public ?? true,
          is_highlighted: data.is_highlighted ?? false,
          badge_text: data.badge_text,
          display_order: data.display_order || 0,
        })
        .select()
        .single()

      if (error) throw error
      return { data: plan }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'plans'] })
    },
  })
}

export function useUpdatePlan() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdatePlanData }) => {
      const updateData: Record<string, unknown> = {}

      if (data.name !== undefined) updateData.name = data.name
      if (data.description !== undefined) updateData.description = data.description
      if (data.price_monthly_cents !== undefined) {
        updateData.price_monthly_cents = data.price_monthly_cents
        updateData.price_cents = data.price_monthly_cents // backward compat
      }
      if (data.price_yearly_cents !== undefined) updateData.price_yearly_cents = data.price_yearly_cents
      if (data.billing_model !== undefined) updateData.billing_model = data.billing_model
      if (data.account_scope !== undefined) updateData.account_scope = data.account_scope
      if (data.max_units !== undefined) updateData.max_units = data.max_units
      if (data.max_professionals !== undefined) updateData.max_professionals = data.max_professionals
      if (data.max_patients !== undefined) updateData.max_patients = data.max_patients
      if (data.max_reports_per_month !== undefined) updateData.max_reports_per_month = data.max_reports_per_month
      if (data.max_storage_mb !== undefined) updateData.max_storage_mb = data.max_storage_mb
      if (data.features !== undefined) updateData.features = data.features
      if (data.trial_days !== undefined) updateData.trial_days = data.trial_days
      if (data.is_public !== undefined) updateData.is_public = data.is_public
      if (data.is_highlighted !== undefined) updateData.is_highlighted = data.is_highlighted
      if (data.badge_text !== undefined) updateData.badge_text = data.badge_text
      if (data.display_order !== undefined) updateData.display_order = data.display_order

      const { data: plan, error } = await supabase
        .from('subscription_plans')
        .update(updateData)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return { data: plan }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'plans'] })
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'plan', variables.id] })
    },
  })
}

export function useDeactivatePlan() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { data: plan, error } = await supabase
        .from('subscription_plans')
        .update({ is_active: false })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return { data: plan }
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'plans'] })
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'plan', id] })
    },
  })
}

export function useReactivatePlan() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { data: plan, error } = await supabase
        .from('subscription_plans')
        .update({ is_active: true })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return { data: plan }
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'plans'] })
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'plan', id] })
    },
  })
}
